// ----- Phase phase-publish-as-mode (TODO-022) — Publish wizard -----
//
// In-place wizard that takes over the Compiler workspace when the user
// clicks Compile. Replaces the prior modal stack
// (PrePublishChecklistModal → ESignatureReauthModal → PublishIntentModal
// → UnplacedFormsWarningModal → SequenceOrderWarningModal → confirm-
// compile modal) with a guided 4-step flow on the same route + screen.
//
// Per PRODUCT_PRINCIPLES_v1: the compliance workflow should be a guided
// wizard that takes over the Compiler workspace during publish — not
// stacked modals.
//
// Steps:
//   1. Pre-publish checklist (auto items + manual sign-off)
//   2. Review summary (read-only metadata + confirmation statement)
//   3. E-signature (MSAL re-auth — gated by the submission's resolved `reAuth` governance)
//   4. Compiling (progress, then "Package ready" with download or error)
//
// Backend contract unchanged: the wizard assembles the same compile body
// the previous modal chain did (publishMode + checklistAcknowledgments +
// signatureAttestation + overrideReason + un/seqorder acks) and calls
// `v2.submissions.compile(submissionId, body)`. The 4-step grouping is
// purely a frontend re-organization.
//
// T-17 (T17-D2/D14) — governance (`preP`/`reAuth`) AND the checklist items come
// from ONE endpoint (GET /submissions/:id/prepublish-checklist, via
// `usePublishGovernance`). The wizard renders only after that fetch resolved;
// a fetch error FAILS CLOSED (error card + Retry, no way to continue).

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import {
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
  v2,
  ApiError,
  type CompiledPackageSummary,
  type Submission,
  type SubmissionValidationResultsResponse,
  parseApiErrorBody,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { describeApiError } from '@/lib/apiErrorView';
import { classifyCompile422Text, type Compile422 } from '@/lib/compiler/compileBlock';
import { captureDevAttestation } from '@/lib/compiler/devAttestation';
import {
  type ChecklistItem,
  type PublishGovernanceSettings,
  type SignatureAttestation,
  type SubmissionPublishContext,
} from '@/lib/compiler/publishGovernanceFlow';
import { usePublishGovernance } from '@/hooks/usePublishGovernance';
import { apiLoginRequest } from '@/lib/auth/msalConfig';
import { isAuthEnabled } from '@/env';
import { CompileBlockNotice, type CompileBlockTestIds } from './CompileBlockNotice';

/** T-17 (T17-D9) — one panel per non-overridable 422 kind. */
const WIZARD_BLOCK_TEST_IDS: CompileBlockTestIds = {
  incomplete: 'publish-wizard-block-incomplete',
  'checklist-auto': 'publish-wizard-block-checklist',
  'no-confirmed': 'publish-wizard-block-no-confirmed',
  'slot-format': 'publish-wizard-block-slot-format',
};

// ----- Props ----- //

export interface PublishWizardProps {
  readonly submissionId: string;
  /** Full submission row for the review-summary step. */
  readonly submission: Submission | null;
  /** Latest validation snapshot — feeds doc count + error/warning labels. */
  readonly validation: SubmissionValidationResultsResponse | null;
  /** Submission-side warnings (unplaced forms + seq-order) folded into Step 1. */
  readonly publishContext?: SubmissionPublishContext;
  /** Wizard exit — fired on cancel from any step (returns user to assembly view). */
  readonly onCancel: () => void;
  /** Wizard exit — fired after a successful compile + download. */
  readonly onComplete: () => void;
}

const DEFAULT_CONTEXT: SubmissionPublishContext = {
  unplacedFormsCount: 0,
  outOfSequenceOrder: false,
};

// ----- Step model ----- //

type StepKey = 'checklist' | 'review' | 'esig' | 'compile';

interface StepDef {
  readonly key: StepKey;
  readonly label: string;
}

/**
 * Build the ordered step list.
 *
 * Phase phase-ui-consistency-sweep (§4.3) — the pre-publish checklist step
 * is now gated on the resolved `preP` governance, matching how the e-sig step is
 * already gated on `reAuth`. Previously the checklist was hard-coded as
 * step 1, so it appeared even when the "Show pre-publish checklist before
 * publishing" setting was Off — the setting and the wizard disagreed.
 */
function buildSteps(gov: PublishGovernanceSettings): StepDef[] {
  const steps: StepDef[] = [];
  if (gov.preP) {
    steps.push({ key: 'checklist', label: 'Pre-publish checklist' });
  }
  steps.push({ key: 'review', label: 'Review summary' });
  if (gov.reAuth) {
    steps.push({ key: 'esig', label: 'E-signature' });
  }
  steps.push({ key: 'compile', label: 'Compile' });
  return steps;
}

// ----- Styles ----- //

const wrapperStyle: CSSProperties = {
  background: 'var(--surface, #ffffff)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 24,
  minHeight: 480,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 12,
  borderBottom: '1px solid var(--border)',
};

const stepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: 'var(--text-muted)',
};

const stepDotStyle = (active: boolean, done: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: active ? 'var(--accent)' : done ? 'var(--success, #16a34a)' : 'var(--surface2)',
  color: active || done ? '#fff' : 'var(--text-muted)',
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0,
});

const stepLabelStyle = (active: boolean): CSSProperties => ({
  color: active ? 'var(--text)' : 'var(--text-muted)',
  fontWeight: active ? 600 : 400,
});

const bodyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '8px 0',
};

const buttonRowStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  justifyContent: 'space-between',
  paddingTop: 12,
  borderTop: '1px solid var(--border)',
};

const primaryButtonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '8px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
};

const warningBoxStyle: CSSProperties = {
  padding: 12,
  background: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.4)',
  borderRadius: 6,
  fontSize: 13,
  color: '#92400e',
};

const errorBoxStyle: CSSProperties = {
  padding: 12,
  background: 'rgba(185, 28, 28, 0.05)',
  border: '1px solid var(--danger, #b91c1c)',
  borderRadius: 6,
  color: 'var(--danger, #b91c1c)',
  fontSize: 13,
};

// ----- Component ----- //

export function PublishWizard(props: PublishWizardProps): ReactElement {
  const gov = usePublishGovernance(props.submissionId);

  // One stable root element across loading -> ready, so the DOM node (and any
  // reference to it) survives the transition.
  if (gov.status === 'ready') {
    return (
      <div data-testid="publish-wizard" style={wrapperStyle}>
        <PublishWizardBody
          {...props}
          governance={gov.data.governance}
          served={gov.data.items}
          refreshChecklist={gov.refresh}
        />
      </div>
    );
  }

  // T-17 (T17-D2) — governance not known yet: do NOT render the flow. Loading
  // shows a status line; an error is a fail-CLOSED card (the policy could not
  // be read, which is not the same as "the policy is off").
  return (
    <div data-testid="publish-wizard" style={wrapperStyle}>
      <header style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Publish — guided workflow</h2>
      </header>
      <div style={bodyStyle}>
        {gov.status === 'loading' ? (
          <div
            data-testid="publish-wizard-governance-loading"
            style={{ fontSize: 13, color: 'var(--text-muted)' }}
          >
            Loading publish requirements…
          </div>
        ) : (
          <div
            role="alert"
            data-testid="publish-wizard-governance-error"
            style={errorBoxStyle}
          >
            <div style={{ fontWeight: 600 }}>
              Could not load this submission's publish requirements.
            </div>
            <div style={{ marginTop: 4 }}>
              {/* An ApiError whose body is not JSON would echo the raw body: use the fallback. */}
              {gov.error instanceof ApiError && parseApiErrorBody(gov.error) === null
                ? 'Please retry. Publishing is blocked until they load.'
                : formatApiError(gov.error, 'Please retry. Publishing is blocked until they load.')}
            </div>
            <button
              type="button"
              data-testid="publish-wizard-governance-retry"
              onClick={gov.reload}
              style={{ ...secondaryButtonStyle, marginTop: 10 }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
      <footer style={buttonRowStyle}>
        <button
          type="button"
          data-testid="publish-wizard-cancel"
          onClick={props.onCancel}
          style={secondaryButtonStyle}
        >
          ← Cancel
        </button>
      </footer>
    </div>
  );
}

interface PublishWizardBodyProps extends PublishWizardProps {
  readonly governance: { readonly preP: boolean; readonly reAuth: boolean };
  /** Checklist items exactly as the server served them (auto first, then manual). */
  readonly served: ReadonlyArray<ChecklistItem>;
  /** Re-fetch the served items without leaving the checklist (after a validation run). */
  readonly refreshChecklist: () => Promise<void>;
}

function PublishWizardBody({
  submissionId,
  submission,
  validation,
  governance,
  served,
  refreshChecklist,
  publishContext = DEFAULT_CONTEXT,
  onCancel,
  onComplete,
}: PublishWizardBodyProps): ReactElement {
  const publishGovernanceSettings = useMemo<PublishGovernanceSettings>(
    () => ({ preP: governance.preP, reAuth: governance.reAuth }),
    [governance.preP, governance.reAuth],
  );
  const steps = useMemo(
    () => buildSteps(publishGovernanceSettings),
    [publishGovernanceSettings],
  );
  const [stepIdx, setStepIdx] = useState(0);
  const currentStep = steps[stepIdx]?.key ?? 'checklist';

  // --- Step 1: checklist state --- //
  const [manualAcks, setManualAcks] = useState<Record<string, boolean>>({});

  // --- Step 3: e-sig state --- //
  const [signature, setSignature] = useState<SignatureAttestation | null>(null);

  // --- Publish intent (test vs final) --- //
  // phase-fix-publish-wizard-mode — the wizard previously HARD-CODED
  // publishMode:'final' and offered no choice (the modal chain it replaced
  // collected it in PublishIntentModal). The Review step now owns the choice;
  // null means "not chosen yet" and no compile request may leave the client.
  const [publishIntent, setPublishIntent] = useState<'test' | 'final' | null>(null);

  // --- Step 4: compile state --- //
  const [compileBusy, setCompileBusy] = useState(false);
  // phase-fix-publish-wizard-mode — structured failure, not a raw string: the
  // old `compileError = err.message` put the SERVER'S RAW JSON BODY in the UI
  // (ApiError.message IS the body text). `retryable` gates the Retry button —
  // a 4xx precondition failure can never succeed on retry.
  const [compileFailure, setCompileFailure] = useState<{
    message: string;
    correlationId: string | null;
    retryable: boolean;
  } | null>(null);
  // T-17 (T17-D9) — ONE state for "the server refused with a 422", classified
  // by the shared `classifyCompile422Text`. Each kind renders its own panel; only
  // `gate` offers the override. (`unknown` is not stored here: it becomes the
  // honest `compileFailure` card.)
  const [compileBlock, setCompileBlock] = useState<Exclude<Compile422, { kind: 'unknown' }> | null>(
    null,
  );
  const [overrideReason, setOverrideReason] = useState('');
  const [pkg, setPkg] = useState<CompiledPackageSummary | null>(null);

  // Step-1 items are exactly what the server served: the auto items it evaluated
  // and the manual items it requires acknowledged. The operator toggles only the
  // manual ones; the ack map is keyed by their (server) ids.
  const manualItems = useMemo(() => served.filter((i) => i.kind === 'manual'), [served]);
  const autoItemsLive = useMemo(() => served.filter((i) => i.kind === 'auto'), [served]);
  // A required auto item that is not a clean pass (fail OR not evaluated) blocks.
  const requiredAutoFailing = autoItemsLive.some((i) => i.required && i.passed !== true);
  // T-17 (T17-D3): validation that never ran is UNKNOWN — rendered as "Not run
  // yet", offered a run, and (being a required auto item) blocking Continue.
  const validationNotRun = autoItemsLive.some(
    (i) => i.id === 'validation-passed' && i.state === 'unknown',
  );
  const [validationRunning, setValidationRunning] = useState(false);
  const [validationRunError, setValidationRunError] = useState<string | null>(null);
  const runValidation = async (): Promise<void> => {
    setValidationRunning(true);
    setValidationRunError(null);
    try {
      await v2.submissions.validateNow(submissionId);
    } catch (err: unknown) {
      setValidationRunError(
        describeApiError(err, 'Validation could not be run. Try again.').message,
      );
      setValidationRunning(false);
      return;
    }
    // The run finished: re-read the verdict from the server (soft refresh keeps ticks).
    await refreshChecklist();
    setValidationRunning(false);
  };
  const allManualChecked = manualItems.every((i) => manualAcks[i.id] === true);
  const step1Ready = allManualChecked && !requiredAutoFailing;

  // Step-2 review state: confirmation checkbox.
  const [reviewConfirmed, setReviewConfirmed] = useState(false);

  // --- E-sig (Step 3) — MSAL re-auth handler --- //
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const [esigBusy, setEsigBusy] = useState(false);
  const [esigError, setEsigError] = useState<string | null>(null);

  const handleReauth = useCallback(async (): Promise<void> => {
    setEsigBusy(true);
    setEsigError(null);
    try {
      if (!isAuthEnabled()) {
        // Dev-bypass: a fake JWT the dev-mode backend accepts, for the SIGNED-IN
        // user (T17-D36 — the one builder in lib/compiler/devAttestation.ts).
        setSignature(await captureDevAttestation());
        return;
      }
      if (!account) {
        setEsigError('No active account. Please sign in again before publishing.');
        return;
      }
      const result: AuthenticationResult = await instance.acquireTokenPopup({
        ...apiLoginRequest,
        prompt: 'login',
        account,
      });
      const claims = (result.idTokenClaims ?? {}) as {
        iat?: number;
        oid?: string;
        email?: string;
        preferred_username?: string;
      };
      if (typeof claims.iat !== 'number' || typeof claims.oid !== 'string') {
        setEsigError('Re-authentication succeeded but the token is missing iat/oid claims.');
        return;
      }
      const signerEmail = claims.email ?? claims.preferred_username ?? account.username;
      setSignature({
        signerOid: claims.oid,
        signerEmail,
        signatureTimestampUtc: new Date(claims.iat * 1000).toISOString(),
        signatureMeaning: 'approval-for-publication',
        attestationToken: result.idToken,
      });
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        setEsigError('Interactive sign-in was required but not completed. Please try again.');
      } else if (err instanceof Error && err.message.toLowerCase().includes('popup')) {
        setEsigError(
          'Pop-up was blocked. Allow pop-ups for this site, then retry.',
        );
      } else {
        setEsigError(err instanceof Error ? err.message : 'Re-authentication failed.');
      }
    } finally {
      setEsigBusy(false);
    }
  }, [account, instance]);

  // --- Compile (Step 4) — call API + handle 422 + render result --- //

  const submitCompile = useCallback(async (): Promise<void> => {
    // Guarantee: no compile request leaves the client without a mode. A
    // mode-less state renders the choice (CompileStep), never a doomed request.
    if (publishIntent === null) return;
    setCompileBusy(true);
    setCompileFailure(null);
    try {
      const body: Parameters<typeof v2.submissions.compile>[1] = {
        publishMode: publishIntent,
        // T-17 (T17-D9) — no unplaced-forms / sequence-order ack booleans: the
        // wizard is given no publishContext, so it could only send values it did
        // not compute, and the server has no evaluator for them yet (T17-D25).
      };
      if (overrideReason.trim()) body.overrideReason = overrideReason.trim();
      if (signature) {
        body.signatureAttestation = {
          signerOid: signature.signerOid,
          signerEmail: signature.signerEmail,
          signatureTimestampUtc: signature.signatureTimestampUtc,
          signatureMeaning: signature.signatureMeaning,
          attestationToken: signature.attestationToken,
        };
      }
      // Only when the checklist step ran (preP): exactly the served manual ids
      // the operator ticked, all `true` — the server rejects anything else.
      if (governance.preP) {
        body.checklistAcknowledgments = Object.fromEntries(
          manualItems.filter((i) => manualAcks[i.id] === true).map((i) => [i.id, true]),
        );
      }
      const res = await v2.submissions.compile(submissionId, body);
      setPkg(res.package);
      setCompileBlock(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const block = classifyCompile422Text(err.body);
        if (block.kind === 'unknown') {
          setCompileBlock(null);
          setCompileFailure({
            message:
              block.message ?? 'Compile was blocked, but the server response could not be read.',
            correlationId: null,
            // A readable refusal is a precondition an identical retry cannot fix.
            retryable: block.message === null,
          });
        } else {
          setCompileBlock(block);
        }
      } else if (err instanceof ApiError) {
        // Honest-error idiom (0193f49/eaed2b8): the server's message is already
        // human via safeErrorHandler — show IT, plus the correlation id when
        // present, never the raw body. A 4xx is a precondition/permission
        // failure that an identical retry cannot fix; only 5xx retries.
        const parsed = parseApiErrorBody(err);
        const retryable = err.status >= 500;
        setCompileFailure({
          message:
            parsed?.error === 'MissingPublishMode'
              ? 'Choose whether this is a test or final compile before compiling.'
              : (parsed?.message ??
                `Compile failed (HTTP ${err.status}). Please try again or contact support.`),
          correlationId:
            typeof (parsed as { correlationId?: unknown } | null)?.correlationId === 'string'
              ? ((parsed as { correlationId: string }).correlationId)
              : null,
          retryable,
        });
      } else {
        // Network / unexpected — genuinely retryable.
        setCompileFailure({
          message:
            err instanceof Error && err.message
              ? err.message
              : 'Compile failed — the request did not reach the server.',
          correlationId: null,
          retryable: true,
        });
      }
    } finally {
      setCompileBusy(false);
    }
  }, [
    submissionId,
    overrideReason,
    signature,
    manualAcks,
    manualItems,
    governance.preP,
    publishIntent,
  ]);

  // Auto-trigger the compile call when the user advances into Step 4
  // (unless we're already showing a result / error). Avoids a redundant
  // "Click Compile to start" intermediate state.
  useEffect(() => {
    if (currentStep !== 'compile') return;
    // No mode chosen → the CompileStep renders the choice; nothing fires.
    if (publishIntent === null) return;
    if (compileBusy || pkg || compileFailure || compileBlock) return;
    void submitCompile();
    // submitCompile is stable enough — depending on it would re-trigger
    // every time overrideReason changes, which is wrong.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, publishIntent]);

  // --- Step navigation guards --- //
  const canAdvance = (): boolean => {
    if (currentStep === 'checklist') return step1Ready;
    // Review owns the test-vs-final choice: no mode, no advancing to compile.
    if (currentStep === 'review') return reviewConfirmed && publishIntent !== null;
    if (currentStep === 'esig') return signature !== null;
    return false;
  };

  const goNext = (): void => {
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
  };
  const goBack = (): void => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  // ----- Render ----- //

  return (
    <>
      <header style={headerStyle}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Publish — guided workflow</h2>
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
            Step <strong data-testid="publish-wizard-step-num">{stepIdx + 1}</strong>{' '}
            of <strong>{steps.length}</strong>: {steps[stepIdx]?.label}
          </div>
        </div>
        <div style={stepperStyle} aria-label="Publish progress">
          {steps.map((s, i) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={stepDotStyle(i === stepIdx, i < stepIdx)}>{i + 1}</span>
              <span style={stepLabelStyle(i === stepIdx)}>{s.label}</span>
              {i < steps.length - 1 && (
                <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>→</span>
              )}
            </div>
          ))}
        </div>
      </header>

      <div style={bodyStyle}>
        {currentStep === 'checklist' && (
          <ChecklistStep
            autoItems={autoItemsLive}
            manualItems={manualItems}
            manualAcks={manualAcks}
            setManualAcks={setManualAcks}
            publishContext={publishContext}
            onRunValidation={() => void runValidation()}
            validationRunning={validationRunning}
            validationRunError={validationRunError}
          />
        )}
        {currentStep === 'review' && (
          <ReviewStep
            submission={submission}
            validation={validation}
            validationNotRun={validationNotRun}
            confirmed={reviewConfirmed}
            setConfirmed={setReviewConfirmed}
            publishIntent={publishIntent}
            setPublishIntent={setPublishIntent}
          />
        )}
        {currentStep === 'esig' && (
          <EsigStep
            account={account}
            signature={signature}
            esigBusy={esigBusy}
            esigError={esigError}
            handleReauth={handleReauth}
          />
        )}
        {currentStep === 'compile' && (
          <CompileStep
            submissionId={submissionId}
            busy={compileBusy}
            pkg={pkg}
            compileFailure={compileFailure}
            compileBlock={compileBlock}
            hasAttestation={signature !== null}
            overrideReason={overrideReason}
            setOverrideReason={setOverrideReason}
            publishIntent={publishIntent}
            setPublishIntent={setPublishIntent}
            onRetry={() => void submitCompile()}
            onDoneAfterSuccess={onComplete}
          />
        )}
      </div>

      <footer style={buttonRowStyle}>
        <button
          type="button"
          data-testid="publish-wizard-cancel"
          onClick={onCancel}
          disabled={compileBusy}
          style={secondaryButtonStyle}
        >
          ← Cancel
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {stepIdx > 0 && currentStep !== 'compile' && (
            <button
              type="button"
              data-testid="publish-wizard-back"
              onClick={goBack}
              style={secondaryButtonStyle}
            >
              ← Back
            </button>
          )}
          {currentStep !== 'compile' && (
            <button
              type="button"
              data-testid="publish-wizard-next"
              onClick={goNext}
              disabled={!canAdvance()}
              style={{
                ...primaryButtonStyle,
                opacity: canAdvance() ? 1 : 0.5,
                cursor: canAdvance() ? 'pointer' : 'not-allowed',
              }}
            >
              {currentStep === 'esig' ? 'Sign & Compile →' : 'Continue →'}
            </button>
          )}
        </div>
      </footer>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  Step 1 — Pre-publish checklist
// ────────────────────────────────────────────────────────────────────────

interface ChecklistStepProps {
  autoItems: ReadonlyArray<ChecklistItem>;
  manualItems: ReadonlyArray<ChecklistItem>;
  manualAcks: Record<string, boolean>;
  setManualAcks: (next: Record<string, boolean>) => void;
  publishContext: SubmissionPublishContext;
  onRunValidation: () => void;
  validationRunning: boolean;
  validationRunError: string | null;
}

function ChecklistStep({
  autoItems,
  manualItems,
  manualAcks,
  setManualAcks,
  publishContext,
  onRunValidation,
  validationRunning,
  validationRunError,
}: ChecklistStepProps): ReactElement {
  return (
    <>
      <h3 style={sectionTitleStyle}>Pre-publish checklist</h3>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
        Auto-checks reflect current submission state. Manually confirm each
        item below before continuing.
      </p>

      {autoItems.length > 0 && (
        <ul
          data-testid="publish-wizard-auto-items"
          style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {autoItems.map((item) => (
            <li
              key={item.id}
              data-testid={`publish-wizard-auto-${item.id}`}
              data-state={item.state}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
                {/* Unknown is a neutral dash — never a check or a cross. */}
                {item.passed === true ? '✅' : item.passed === false ? '❌' : '—'}
              </span>
              <div>
                <div>{item.label}</div>
                {item.passed === false && (item.detail ?? item.failureMessage) && (
                  <div style={{ fontSize: 12, color: 'var(--danger, #b91c1c)' }}>
                    {item.detail ?? item.failureMessage}
                  </div>
                )}
                {item.state === 'unknown' && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {item.id === 'validation-passed'
                      ? 'Not run yet'
                      : (item.detail ?? 'Not evaluated yet')}
                    {item.id === 'validation-passed' && item.detail ? ` — ${item.detail}` : ''}
                  </div>
                )}
                {item.id === 'validation-passed' && item.state === 'unknown' && (
                  <>
                    <button
                      type="button"
                      data-testid="publish-wizard-run-validation"
                      onClick={onRunValidation}
                      disabled={validationRunning}
                      style={{ ...secondaryButtonStyle, marginTop: 6 }}
                    >
                      {validationRunning ? 'Running validation…' : 'Run validation now'}
                    </button>
                    {validationRunError && (
                      <div
                        role="alert"
                        data-testid="publish-wizard-run-validation-error"
                        style={{ fontSize: 12, color: 'var(--danger, #b91c1c)', marginTop: 4 }}
                      >
                        {validationRunError}
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {(publishContext.unplacedFormsCount > 0 || publishContext.outOfSequenceOrder) && (
        <div data-testid="publish-wizard-warnings" style={warningBoxStyle}>
          {publishContext.unplacedFormsCount > 0 && (
            <div>
              ⚠ {publishContext.unplacedFormsCount} admin form
              {publishContext.unplacedFormsCount === 1 ? '' : 's'} have no eCTD
              section placement. Continuing will publish without them.
            </div>
          )}
          {publishContext.outOfSequenceOrder && (
            <div style={{ marginTop: 8 }}>
              ⚠ This submission's sequence is out of order. Earlier sequences
              for the same NDA may still be unlocked.
            </div>
          )}
        </div>
      )}

      <ul
        data-testid="publish-wizard-manual-items"
        style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {manualItems.map((item) => (
          <li key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <input
              id={`publish-wizard-manual-${item.id}`}
              type="checkbox"
              data-testid={`publish-wizard-manual-${item.id}`}
              checked={manualAcks[item.id] === true}
              onChange={(e) =>
                setManualAcks({ ...manualAcks, [item.id]: e.target.checked })
              }
              style={{ marginTop: 3 }}
            />
            <label htmlFor={`publish-wizard-manual-${item.id}`} style={{ fontSize: 13, cursor: 'pointer' }}>
              {item.label}
            </label>
          </li>
        ))}
      </ul>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  Step 2 — Review summary
// ────────────────────────────────────────────────────────────────────────

interface ReviewStepProps {
  submission: Submission | null;
  validation: SubmissionValidationResultsResponse | null;
  /** T17-D3: the served `validation-passed` item is unknown (never run). */
  validationNotRun: boolean;
  confirmed: boolean;
  setConfirmed: (v: boolean) => void;
  publishIntent: 'test' | 'final' | null;
  setPublishIntent: (m: 'test' | 'final') => void;
}

/**
 * phase-fix-publish-wizard-mode — the test-vs-final choice the wizard's
 * predecessor collected in PublishIntentModal, and the wizard dropped
 * (hard-coding 'final'). The Review step owns it: the user cannot advance to
 * the Compile step without deciding. Copy mirrors PublishIntentModal.
 */
function PublishIntentChoice({
  publishIntent,
  setPublishIntent,
}: {
  publishIntent: 'test' | 'final' | null;
  setPublishIntent: (m: 'test' | 'final') => void;
}): ReactElement {
  const cardStyle = (selected: boolean, tint: string): CSSProperties => ({
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 6,
    border: selected ? '1px solid var(--accent)' : '1px solid var(--border)',
    background: selected ? tint : 'var(--surface)',
    cursor: 'pointer',
    flex: 1,
  });
  return (
    <div data-testid="publish-wizard-intent" style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Create a final package, or a test package for inspection?
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={cardStyle(publishIntent === 'test', 'rgba(245,158,11,0.10)')}>
          <input
            type="radio"
            name="publish-intent"
            data-testid="publish-wizard-mode-test"
            checked={publishIntent === 'test'}
            onChange={() => setPublishIntent('test')}
            style={{ marginTop: 2 }}
          />
          <span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Test publication</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Compiles the package for inspection. Does not mark the sequence as
              submitted; test runs can be filtered out.
            </div>
          </span>
        </label>
        <label style={cardStyle(publishIntent === 'final', 'rgba(34,197,94,0.10)')}>
          <input
            type="radio"
            name="publish-intent"
            data-testid="publish-wizard-mode-final"
            checked={publishIntent === 'final'}
            onChange={() => setPublishIntent('final')}
            style={{ marginTop: 2 }}
          />
          <span>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Final publication</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Requires a complete compile and locks the compiled sequence.
              Transmission and agency acceptance are separate steps.
            </div>
          </span>
        </label>
      </div>
    </div>
  );
}

function ReviewStep({
  submission,
  validation,
  validationNotRun,
  confirmed,
  setConfirmed,
  publishIntent,
  setPublishIntent,
}: ReviewStepProps): ReactElement {
  return (
    <>
      <h3 style={sectionTitleStyle}>Review summary</h3>
      <dl
        data-testid="publish-wizard-summary"
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr',
          gap: '6px 16px',
          margin: 0,
          fontSize: 13,
        }}
      >
        <dt style={{ color: 'var(--text-muted)' }}>Drug name</dt>
        <dd data-testid="publish-wizard-drug" style={{ margin: 0 }}>
          {submission?.DrugName ?? '(unknown)'}
        </dd>
        <dt style={{ color: 'var(--text-muted)' }}>Sequence #</dt>
        <dd data-testid="publish-wizard-seq" style={{ margin: 0 }}>
          {submission?.SequenceNumber ?? '(unknown)'}
        </dd>
        <dt style={{ color: 'var(--text-muted)' }}>Region</dt>
        <dd data-testid="publish-wizard-region" style={{ margin: 0 }}>
          {submission?.Region ?? '(unknown)'}
        </dd>
        <dt style={{ color: 'var(--text-muted)' }}>Documents</dt>
        <dd data-testid="publish-wizard-doc-count" style={{ margin: 0 }}>
          {validation?.documentCount ?? '?'}
        </dd>
        {validationNotRun && (
          <>
            <dt style={{ color: 'var(--text-muted)' }}>Validation</dt>
            <dd data-testid="publish-wizard-validation-summary" style={{ margin: 0 }}>
              Not run yet
            </dd>
          </>
        )}
        {!validationNotRun && validation && (
          <>
            <dt style={{ color: 'var(--text-muted)' }}>Validation</dt>
            <dd style={{ margin: 0 }}>
              {validation.errorCount} error{validation.errorCount === 1 ? '' : 's'},{' '}
              {validation.warningCount} warning{validation.warningCount === 1 ? '' : 's'},{' '}
              {validation.passCount} pass
            </dd>
          </>
        )}
      </dl>

      <PublishIntentChoice publishIntent={publishIntent} setPublishIntent={setPublishIntent} />

      <label
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          fontSize: 13,
          marginTop: 8,
          padding: 12,
          background: 'var(--surface2, rgba(0,0,0,0.02))',
          borderRadius: 6,
        }}
      >
        <input
          type="checkbox"
          data-testid="publish-wizard-review-confirm"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span>I confirm this package is complete and ready for submission.</span>
      </label>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  Step 3 — E-signature
// ────────────────────────────────────────────────────────────────────────

interface EsigStepProps {
  account: { username?: string } | null;
  signature: SignatureAttestation | null;
  esigBusy: boolean;
  esigError: string | null;
  handleReauth: () => Promise<void>;
}

function EsigStep({
  account,
  signature,
  esigBusy,
  esigError,
  handleReauth,
}: EsigStepProps): ReactElement {
  return (
    <>
      <h3 style={sectionTitleStyle}>Confirm your identity to publish</h3>
      <p style={{ margin: 0, fontSize: 13 }}>
        Publishing creates a permanent, regulator-facing record. Re-enter
        your credentials to confirm this is a deliberate act (21 CFR Part 11 §11.50).
      </p>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Signed in as: <code data-testid="publish-wizard-esig-account">{account?.username ?? '(no account)'}</code>
        <br />
        Device time: <code>{new Date().toISOString()}</code>
      </div>

      {signature && (
        <div
          data-testid="publish-wizard-esig-captured"
          style={{
            padding: 10,
            background: 'rgba(22,163,74,0.08)',
            border: '1px solid rgba(22,163,74,0.4)',
            borderRadius: 6,
            fontSize: 12,
            color: '#15803d',
          }}
        >
          ✓ Signature captured at <code>{signature.signatureTimestampUtc}</code> for{' '}
          <code>{signature.signerEmail}</code>. Click "Sign & Compile →" to proceed.
        </div>
      )}

      {esigError && (
        <div data-testid="publish-wizard-esig-error" role="alert" style={errorBoxStyle}>
          {esigError}
        </div>
      )}

      {!signature && (
        <button
          type="button"
          data-testid="publish-wizard-esig-reauth"
          disabled={esigBusy}
          onClick={() => void handleReauth()}
          style={{
            ...primaryButtonStyle,
            alignSelf: 'flex-start',
            opacity: esigBusy ? 0.6 : 1,
            cursor: esigBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {esigBusy ? 'Re-authenticating…' : 'Re-authenticate'}
        </button>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  Step 4 — Compile
// ────────────────────────────────────────────────────────────────────────

interface CompileStepProps {
  submissionId: string;
  busy: boolean;
  pkg: CompiledPackageSummary | null;
  compileFailure: { message: string; correlationId: string | null; retryable: boolean } | null;
  compileBlock: Exclude<Compile422, { kind: 'unknown' }> | null;
  /** A signature attestation was captured and sent (only then may the UI say "signed"). */
  hasAttestation: boolean;
  overrideReason: string;
  setOverrideReason: (v: string) => void;
  publishIntent: 'test' | 'final' | null;
  setPublishIntent: (m: 'test' | 'final') => void;
  onRetry: () => void;
  onDoneAfterSuccess: () => void;
}

function CompileStep({
  submissionId,
  busy,
  pkg,
  compileFailure,
  compileBlock,
  hasAttestation,
  overrideReason,
  setOverrideReason,
  publishIntent,
  setPublishIntent,
  onRetry,
  onDoneAfterSuccess,
}: CompileStepProps): ReactElement {
  // T17-D8 — the package download goes through the authenticated client
  // (a bare <a href> carried no Authorization header).
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // phase-fix-publish-wizard-mode — a mode-less state renders the CHOICE, never
  // a doomed request. Reachable only if the step was entered without the Review
  // gate (state reset, programmatic entry); choosing a mode auto-fires the
  // compile via the parent's step-entry effect.
  if (publishIntent === null && !pkg && !busy) {
    return (
      <div data-testid="publish-wizard-mode-required" style={{ maxWidth: 640 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          One decision before compiling
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
          Choose whether this is a test or final compile. Nothing is sent until
          you decide.
        </div>
        <PublishIntentChoice publishIntent={publishIntent} setPublishIntent={setPublishIntent} />
      </div>
    );
  }

  if (busy) {
    return (
      <div data-testid="publish-wizard-compiling" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Compiling submission…</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Renaming files per ICH M8, computing checksums, assembling the
          eCTD backbone.
        </div>
      </div>
    );
  }

  if (pkg) {
    const onDownloadZip = async (): Promise<void> => {
      setDownloadBusy(true);
      setDownloadError(null);
      try {
        await v2.submissions.compileDownload(submissionId, pkg.id);
      } catch (err) {
        setDownloadError(describeApiError(err, 'The package could not be downloaded.').message);
      } finally {
        setDownloadBusy(false);
      }
    };
    return (
      <div
        data-testid="publish-wizard-success"
        data-mode={publishIntent === 'test' ? 'test' : 'final'}
        style={{ textAlign: 'center', padding: 24 }}
      >
        <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
        {/* T-17 (T17-D9) — the heading names the MODE. The old "Submission signed
            and compiled" claimed a signature on every compile (including test
            compiles and preP-off publishes that captured none). "signed" appears
            only when an attestation was actually sent. */}
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          {publishIntent === 'test'
            ? 'Test package compiled — nothing was submitted'
            : hasAttestation
              ? 'Final package compiled — signature attestation recorded'
              : 'Final package compiled'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          {pkg.totalLeafFiles} leaf{pkg.totalLeafFiles === 1 ? '' : 's'} ·
          validation: <strong>{pkg.validationStatus}</strong>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Package creation does not confirm transmission, acknowledgment or agency acceptance.
        </p>
        <div
          style={{
            display: 'inline-flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="publish-wizard-save-draft"
            onClick={onDoneAfterSuccess}
            style={secondaryButtonStyle}
          >
            Return to Compilation
          </button>
          {pkg.hasZip && (
            <button
              type="button"
              data-testid="publish-wizard-download-zip"
              disabled={downloadBusy}
              onClick={() => void onDownloadZip()}
              style={secondaryButtonStyle}
            >
              {downloadBusy ? 'Preparing download…' : 'Download as .zip'}
            </button>
          )}
        </div>
        {downloadError && (
          <div
            role="alert"
            data-testid="publish-wizard-download-error"
            style={{ ...errorBoxStyle, marginTop: 12, textAlign: 'left' }}
          >
            {downloadError}
          </div>
        )}
      </div>
    );
  }

  if (compileBlock && compileBlock.kind !== 'gate') {
    return <CompileBlockNotice block={compileBlock} testIds={WIZARD_BLOCK_TEST_IDS} />;
  }

  if (compileBlock && compileBlock.kind === 'gate') {
    // Only a ValidationGateBlock is overridable (with a recorded reason).
    const errs = compileBlock.errors;
    return (
      <>
        <div data-testid="publish-wizard-gate-errors" style={errorBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {errs.length} validation error
            {errs.length === 1 ? '' : 's'} blocked compile
          </div>
          <ul style={{ margin: '4px 0 8px 16px' }}>
            {errs.slice(0, 5).map((e) => (
              <li key={e.resultId}>
                <code style={{ fontSize: 11 }}>{e.ruleName}</code> ({e.targetType}):{' '}
                {e.message ?? '(no message)'}
              </li>
            ))}
            {errs.length > 5 && <li>…and {errs.length - 5} more</li>}
          </ul>
        </div>
        <label style={{ display: 'block', fontSize: 12 }}>
          Override reason (required to proceed):
          <textarea
            data-testid="publish-wizard-override-reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="Explain why these errors are acceptable. Recorded in the audit log."
            style={{
              width: '100%',
              minHeight: 80,
              padding: 8,
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: 13,
              boxSizing: 'border-box',
              marginTop: 4,
            }}
          />
        </label>
        <button
          type="button"
          data-testid="publish-wizard-override-retry"
          onClick={onRetry}
          disabled={overrideReason.trim().length === 0}
          style={{
            ...primaryButtonStyle,
            alignSelf: 'flex-start',
            opacity: overrideReason.trim().length === 0 ? 0.5 : 1,
            cursor: overrideReason.trim().length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Override + retry compile
        </button>
      </>
    );
  }

  if (compileFailure) {
    return (
      <>
        <div data-testid="publish-wizard-compile-error" role="alert" style={errorBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Compile did not complete</div>
          <div>{compileFailure.message}</div>
          {compileFailure.correlationId && (
            <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
              Reference: <code>{compileFailure.correlationId}</code> — quote this
              when contacting support.
            </div>
          )}
        </div>
        {/* Retry only when an identical retry can actually succeed (network /
            5xx). A 4xx precondition failure gets its precondition, not a
            futile Retry. */}
        {compileFailure.retryable && (
          <button
            type="button"
            data-testid="publish-wizard-retry"
            onClick={onRetry}
            style={{ ...primaryButtonStyle, alignSelf: 'flex-start' }}
          >
            Retry compile
          </button>
        )}
      </>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
      Preparing compile…
    </div>
  );
}
